import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Share2, Copy, RefreshCcw, Eye, Loader2, ExternalLink } from "lucide-react";

/** Compact "Share preview" button for the admin Course Detail page.
 *  Mints (and re-mints) a long-lived public token, lets the admin copy the URL,
 *  or regenerate to revoke a leaked link. */
export default function ShareCourseButton({ courseId }) {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);

  const url = token ? `${window.location.origin}/share/c/${token}` : "";

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/share/courses/${courseId}/regenerate`);
      setToken(data.share_token);
      toast.success("Share link ready", { description: "Copy + paste anywhere — no login required." });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate link");
    } finally { setBusy(false); }
  };

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied", { description: url });
    } catch { toast.error("Copy failed"); }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o && !token) generate(); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 gap-1.5" data-testid="share-course-button">
          <Share2 size={13}/> Share preview
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-4" data-testid="share-course-popover">
        <div className="text-[10px] uppercase tracking-wider text-[#E53935] font-bold mb-1 inline-flex items-center gap-1.5">
          <Eye size={11}/> Public preview link
        </div>
        <h3 className="text-sm font-display font-bold text-slate-900 mb-3">Anyone with this link can preview the course — no login.</h3>
        <div className="flex items-stretch gap-1 mb-3">
          <input
            readOnly
            value={busy ? "Generating link…" : (url || "Click Generate")}
            onFocus={(e) => e.target.select()}
            className="flex-1 min-w-0 px-3 py-2 text-xs font-mono bg-slate-50 border border-slate-200 rounded text-slate-700 truncate"
            data-testid="share-link-url"
          />
          <button
            onClick={copy}
            disabled={!url || busy}
            className="px-3 bg-[#E53935] hover:bg-[#c62828] text-white rounded text-xs flex items-center gap-1 disabled:opacity-40"
            data-testid="share-link-copy"
            title="Copy link"
          >
            <Copy size={12}/>
          </button>
        </div>
        <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
          <button
            onClick={generate}
            disabled={busy}
            className="text-xs text-slate-500 hover:text-[#E53935] inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="share-link-regenerate"
            title="Mint a fresh token — the previous URL stops working"
          >
            {busy ? <Loader2 size={11} className="animate-spin"/> : <RefreshCcw size={11}/>} Regenerate
          </button>
          {url && (
            <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#E53935] hover:underline inline-flex items-center gap-1" data-testid="share-link-open">
              Open <ExternalLink size={11}/>
            </a>
          )}
        </div>
        <p className="text-[10px] text-slate-400 mt-3 leading-relaxed">
          Includes only <strong>published</strong> modules. Quiz answers are visible but cannot be submitted. Regenerating invalidates any previously-shared URL.
        </p>
      </PopoverContent>
    </Popover>
  );
}
